import React, { useState } from "react";

/**
 * Step 5: Review & Go-Live Settings
 * Final review and go-live settings
 */
function Step5ReviewGoLive({
  migrationData,
  updateMigrationData,
  nextStep,
  prevStep,
}) {
  const [goLiveOption, setGoLiveOption] = useState("");
  const [pendingOption, setPendingOption] = useState(null);
  const [showOptionModal, setShowOptionModal] = useState(false);

  const optionDetails = {
    immediate: {
      title: "Start now on OGESTYLE",
      intro:
        "Your import starts right away and your business is activated on OGESTYLE immediately after import.",
      points: [
        "Your trial period starts right away.",
        "Your customers can begin booking as soon as setup requirements are met.",
        "Great if you are ready to move today.",
      ],
      confirmLabel: "Yes, I want to start now",
    },
    scheduled: {
      title: "Take a little time first",
      intro:
        "Your import still runs now, but go-live is scheduled automatically so you can look around and finish setup comfortably.",
      points: [
        "You can familiarize yourself with the platform before going live.",
        "You can keep importing customers while preparing.",
        "Your go-live date is auto-set based on signup and platform rules.",
      ],
      confirmLabel: "Yes, I want a little time first",
    },
  };

  const handleOptionClick = (option) => {
    setPendingOption(option);
    setShowOptionModal(true);
  };

  const confirmOptionSelection = () => {
    if (pendingOption) {
      setGoLiveOption(pendingOption);
    }
    setShowOptionModal(false);
  };

  const handleStartImport = () => {
    if (!goLiveOption) {
      return;
    }

    updateMigrationData({
      goLiveSettings: {
        ...migrationData.goLiveSettings,
        goLiveOption,
      },
    });
    nextStep();
  };

  // Calculate summary stats
  const getFileStats = () => {
    const stats = {};
    Object.entries(migrationData.uploadedFiles).forEach(([type, file]) => {
      if (file) {
        stats[type] = {
          name: file.name,
          size: (file.size / 1024).toFixed(2) + " KB",
        };
      }
    });
    return stats;
  };

  const stats = getFileStats();
  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Step 5: Review & Go-Live</h2>
      <p style={styles.subtitle}>
        Review your import settings and choose how you want to get started.
      </p>

      {/* Import Summary */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Import Summary</h3>
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.icon}>📦</div>
            <div>
              <div style={styles.cardLabel}>Source Platform</div>
              <div style={styles.cardValue}>
                {migrationData.sourcePlatform?.charAt(0).toUpperCase() +
                  migrationData.sourcePlatform?.slice(1)}
              </div>
            </div>
          </div>

          {Object.entries(stats).map(([type, info]) => (
            <div key={type} style={styles.summaryCard}>
              <div style={styles.icon}>
                {type === "services"
                  ? "✂️"
                  : type === "customers"
                    ? "👥"
                    : type === "appointments"
                      ? "📅"
                      : "👤"}
              </div>
              <div>
                <div style={styles.cardLabel}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </div>
                <div style={styles.cardValue}>{info.name}</div>
                <div style={styles.cardSubtext}>{info.size}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Field Mappings Summary */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Field Mappings</h3>
        {Object.entries(migrationData.fieldMappings || {}).map(
          ([fileType, mappings]) => {
            const mappedCount = Object.keys(mappings).length;
            return mappedCount > 0 ? (
              <div key={fileType} style={styles.mappingSummary}>
                <strong>
                  {fileType.charAt(0).toUpperCase() + fileType.slice(1)}:
                </strong>{" "}
                {mappedCount} field{mappedCount > 1 ? "s" : ""} mapped
              </div>
            ) : null;
          },
        )}
      </div>

      {/* Conflicts Summary */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Conflicts</h3>
        <div style={styles.infoBox}>
          {(migrationData.conflicts?.length || 0) === 0 ? (
            <span style={styles.successText}>✓ No conflicts detected</span>
          ) : (
            <span style={styles.warningText}>
              ⚠️ {migrationData.conflicts.length} conflict
              {migrationData.conflicts.length > 1 ? "s" : ""} resolved
            </span>
          )}
        </div>
      </div>

      {/* Go-Live Settings */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>
          When do you want to start running your business on OGESTYLE?
        </h3>
        <p style={styles.sectionHelperText}>
          Choose one option to continue. You will see a quick summary before we
          save your choice.
        </p>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            <input
              type="radio"
              checked={goLiveOption === "immediate"}
              onChange={() => handleOptionClick("immediate")}
              style={styles.radio}
            />
            <span>I want to start now</span>
          </label>
          <p style={styles.helperText}>
            Import now and activate your business right after import.
          </p>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            <input
              type="radio"
              checked={goLiveOption === "scheduled"}
              onChange={() => handleOptionClick("scheduled")}
              style={styles.radio}
            />
            <span>
              I want to take a little time to look around the platform and
              familiarize myself
            </span>
          </label>
          <p style={styles.helperText}>
            Import now and let OGESTYLE auto-schedule go-live for you.
          </p>
        </div>

        {!goLiveOption && (
          <div style={styles.selectionNotice}>
            Please select one option above to continue.
          </div>
        )}

        {goLiveOption && (
          <div style={styles.selectionSummary}>
            Selected: {goLiveOption === "immediate" ? "Start now" : "Take a little time first"}
          </div>
        )}

      </div>

      {/* Warning Notice */}
      <div style={styles.warningBox}>
        <div style={styles.warningIcon}>⚠️</div>
        <div>
          <strong>Important:</strong> This import process cannot be undone.
          Please review all settings carefully before proceeding.
        </div>
      </div>

      <div style={styles.actions}>
        <button onClick={prevStep} style={styles.backButton}>
          ← Back
        </button>
        <button
          onClick={handleStartImport}
          style={
            goLiveOption ? styles.continueButton : styles.continueButtonDisabled
          }
          disabled={!goLiveOption}
        >
          Start Import →
        </button>
      </div>

      {showOptionModal && pendingOption && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <h4 style={styles.modalTitle}>{optionDetails[pendingOption].title}</h4>
            <p style={styles.modalIntro}>{optionDetails[pendingOption].intro}</p>
            <ul style={styles.modalList}>
              {optionDetails[pendingOption].points.map((point) => (
                <li key={point} style={styles.modalListItem}>
                  {point}
                </li>
              ))}
            </ul>

            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.modalSecondaryButton}
                onClick={() => setShowOptionModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={styles.modalPrimaryButton}
                onClick={confirmOptionSelection}
              >
                {optionDetails[pendingOption].confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    marginBottom: "8px",
    color: "#111",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "32px",
  },
  section: {
    marginBottom: "32px",
    paddingBottom: "24px",
    borderBottom: "1px solid #e0e0e0",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "8px",
    color: "#333",
  },
  sectionHelperText: {
    fontSize: "13px",
    color: "#6b7280",
    marginBottom: "16px",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
  },
  summaryCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  icon: {
    fontSize: "32px",
  },
  cardLabel: {
    fontSize: "12px",
    color: "#666",
    marginBottom: "4px",
  },
  cardValue: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
  },
  cardSubtext: {
    fontSize: "11px",
    color: "#999",
    marginTop: "2px",
  },
  mappingSummary: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "8px",
  },
  infoBox: {
    padding: "12px 16px",
    backgroundColor: "#f5f5f5",
    borderRadius: "6px",
    fontSize: "14px",
  },
  successText: {
    color: "#4CAF50",
    fontWeight: "500",
  },
  warningText: {
    color: "#ff9800",
    fontWeight: "500",
  },
  formGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
    marginBottom: "8px",
    cursor: "pointer",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
    cursor: "pointer",
  },
  radio: {
    cursor: "pointer",
  },
  checkbox: {
    marginTop: "2px",
    cursor: "pointer",
  },
  dateInput: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    marginTop: "8px",
  },
  helperText: {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px",
    marginLeft: "24px",
  },
  selectionNotice: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#b45309",
    backgroundColor: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "6px",
    padding: "8px 10px",
  },
  selectionSummary: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#065f46",
    backgroundColor: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: "6px",
    padding: "8px 10px",
    fontWeight: "600",
  },
  warningBox: {
    display: "flex",
    gap: "12px",
    padding: "16px",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#856404",
    marginTop: "24px",
  },
  warningIcon: {
    fontSize: "20px",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "32px",
  },
  backButton: {
    padding: "12px 24px",
    backgroundColor: "#fff",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  continueButton: {
    padding: "12px 32px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  continueButtonDisabled: {
    padding: "12px 32px",
    backgroundColor: "#9ca3af",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "not-allowed",
  },
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(17, 24, 39, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modalCard: {
    width: "100%",
    maxWidth: "560px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.25)",
    padding: "22px",
  },
  modalTitle: {
    margin: "0 0 8px 0",
    fontSize: "20px",
    color: "#111827",
  },
  modalIntro: {
    margin: "0 0 14px 0",
    fontSize: "14px",
    color: "#374151",
    lineHeight: 1.5,
  },
  modalList: {
    margin: "0 0 16px 0",
    paddingLeft: "18px",
    color: "#1f2937",
  },
  modalListItem: {
    marginBottom: "8px",
    fontSize: "14px",
    lineHeight: 1.4,
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  modalSecondaryButton: {
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    backgroundColor: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  modalPrimaryButton: {
    padding: "10px 14px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#16a34a",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default Step5ReviewGoLive;
